import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ImageBackground,
  Image,
  Platform,
  Animated,
  Dimensions,
  Alert,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFloatingButton } from '../contexts/FloatingButtonContext';
import { fetchZoolyfeIdSsoUrl } from '../services/zoolyfeSsoService';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

// on home tab the button stays at the top until the user scrolls past the threshold.
// other tabs always keep it anchored at the bottom.
// if there aren't enough promo cards to trigger a scroll, scrollY never hits the threshold so it stays put.
const SCROLL_THRESHOLD = 80;
const TOP_OFFSET = Platform.OS === 'ios' ? 25 : 5;
const CARD_WIDTH = 77;
const CARD_HEIGHT = 51;
const WA_SIZE = 44;
const BOTTOM_OFFSET = Platform.OS === 'ios' ? 243 : 223;
const GAP_CLOSE_CARD = 8;
const WHATSAPP_URL = 'https://wa.me/6281112203579';
const SHOW_ZOOLYFE_ID_CTA = false;
/** Jarak horizontal antara promo spinning wheel dan tombol WA */
const SPIN_WA_GAP = 12;

export default function FloatingZoolyfeButton() {
  const { scrollY, isHomeFocused, spinFloatingContent } = useFloatingButton();
  const [waVisible, setWaVisible] = useState(true);
  const [animValue] = useState(new Animated.Value(0));

  // snap to bottom when we're not on the home tab, or when the user has scrolled down far enough
  // const useBottomPosition = !isHomeFocused || scrollY > SCROLL_THRESHOLD;
  const useBottomPosition = false;

  useEffect(() => {
    Animated.timing(animValue, {
      toValue: useBottomPosition ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [useBottomPosition, animValue]);

  const handlePress = async () => {
    // open a blank tab right here inside the click handler so the browser
    // doesn't treat the later window.open (after the async fetch) as a popup
    let win = null;
    if (typeof window !== 'undefined') {
      win = window.open('', '_blank');
    }
    try {
      const loginUrl = await fetchZoolyfeIdSsoUrl();
      if (win) {
        win.location.href = loginUrl;
      }
    } catch (error) {
      if (win) win.close();
      Alert.alert(
        'SSO Login Failed',
        error.message || 'Unable to open Zoolyfe.id. Please make sure you are logged in.'
      );
    }
  };

  const handleCloseWa = () => {
    setWaVisible(false);
  };

  const openWhatsApp = () => {
    Linking.openURL(WHATSAPP_URL).catch(() => {});
  };

  const translateY = animValue.interpolate({
    inputRange: [0, 1],
    outputRange: [TOP_OFFSET, SCREEN_HEIGHT - BOTTOM_OFFSET],
  });

  if (scrollY > SCROLL_THRESHOLD) return null;
  if (!waVisible && !spinFloatingContent) return null;

  return (
    <Animated.View
      style={[
        styles.wrapper,
        {
          transform: [{ translateY }],
        },
      ]}
      pointerEvents="box-none"
    >
      <View style={[styles.container, spinFloatingContent ? styles.containerRow : null]}>
        {spinFloatingContent ? (
          <>
            <View style={styles.spinFloatingWrap}>{spinFloatingContent}</View>
            {waVisible ? <View style={{ width: SPIN_WA_GAP }} /> : null}
          </>
        ) : null}
        {waVisible ? (
        <View style={styles.waColumn}>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={handleCloseWa}
          activeOpacity={0.8}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityLabel="Tutup WhatsApp"
        >
          <Ionicons name="close" size={10} color="#1E293B" />
        </TouchableOpacity>

        <View style={styles.gapCloseWa} />

        <TouchableOpacity
          style={styles.waButton}
          onPress={openWhatsApp}
          activeOpacity={0.85}
          accessibilityRole="link"
          accessibilityLabel="Chat WhatsApp"
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
        >
          <Image
            source={require('../../assets/whatsapp-float-icon.png')}
            style={styles.waImage}
            resizeMode="contain"
          />
        </TouchableOpacity>

        {SHOW_ZOOLYFE_ID_CTA && (
          <>
            <View style={styles.gap} />

            <TouchableOpacity
              style={styles.cardTouchable}
              onPress={handlePress}
              activeOpacity={0.9}
            >
              <ImageBackground
                source={require('../../assets/float-zoolife-area.png')}
                style={styles.cardArea}
                imageStyle={styles.cardAreaImage}
                resizeMode="stretch"
              >
                <View style={styles.cardContent}>
                  <Text style={styles.cardTitle}>Belanja Sekarang!</Text>
                  <View style={styles.logoContainer}>
                    <Image
                      source={require('../../assets/float-zoolife.svg')}
                      style={styles.logo}
                      resizeMode="contain"
                    />
                  </View>
                </View>
              </ImageBackground>
            </TouchableOpacity>
          </>
        )}
        </View>
        ) : null}
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    right: 16,
    top: 0,
    zIndex: 999,
    alignItems: 'flex-end',
  },
  container: {
    alignItems: 'flex-end',
  },
  containerRow: {
    flexDirection: 'column-reverse',
    alignItems: 'center',
  },
  waColumn: {
    width: '100%',
    alignItems: 'flex-start',
  },
  spinFloatingWrap: {
    alignItems: 'flex-end',
    backgroundColor: 'transparent',
  },
  closeButton: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  gapCloseWa: {
    height: 6,
  },
  waButton: {
    width: WA_SIZE,
    height: WA_SIZE,
    borderRadius: WA_SIZE / 2,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#0f172a',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.22,
    shadowRadius: 6,
    elevation: 5,
    marginHorizontal: 'auto',
  },
  waImage: {
    width: WA_SIZE,
    height: WA_SIZE,
  },
  gap: {
    height: GAP_CLOSE_CARD,
  },
  cardTouchable: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
  },
  cardArea: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardAreaImage: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
  },
  cardContent: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  cardTitle: {
    fontWeight: '800',
    fontSize: 11.25,
    fontStyle: 'italic',
    color: '#FFFFFF',
    marginBottom: 2,
    textAlign: 'center',
  },
  logo: {
    width: 60,
    height: 13.09,
  },
  logoContainer: {
    width: 60,
    height: 13.09,
    overflow: 'hidden',
  },
});
